import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Select, MenuItem, TextField, Checkbox, FormControlLabel,
  IconButton, Tooltip, Tab, Tabs, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Search as SearchIcon, ArrowDownward as ScrollDownIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { gwtStyles } from '../../config/theme';
import { rpcCall } from '../../api/rpc';
import { api } from '../../api/rpc';
import type { SessionLogEntry, PacsLogEntry } from '../../api/types';

/* ── Severity colors ── */
const severityColor: Record<string, string> = {
  SEVERE: '#ff6b6b',
  WARNING: '#ffa94d',
  INFO: '#74c0fc',
  CONFIG: '#b2bec3',
  FINE: '#a5d8a5',
  FINER: '#90cca0',
  FINEST: '#7ebb8f',
  ALL: '#e0e0e0',
};

const getSeverityColor = (level?: string): string => {
  if (!level) return '#e0e0e0';
  return severityColor[level] ?? '#e0e0e0';
};

const getSeverityColorFromLine = (line: string): string => {
  for (const [level, color] of Object.entries(severityColor)) {
    if (line.includes(level)) return color;
  }
  return '#e0e0e0';
};

/** Format epoch millis to a readable timestamp */
const formatTime = (millis?: number): string => {
  if (!millis) return '';
  const d = new Date(millis);
  return d.toISOString().replace('T', ' ').replace('Z', '');
};

/** Format epoch millis to date + time for the sessions table */
const formatDateTime = (millis?: number): string => {
  if (!millis) return '';
  const d = new Date(millis);
  return d.toLocaleString();
};

/* ── Compact table cell styling ── */
const cellSx = { fontSize: 11, py: 0.25, px: 0.75, borderColor: 'divider', whiteSpace: 'nowrap' as const };
const headerCellSx = { ...cellSx, fontWeight: 600, backgroundColor: 'background.paper' };

/* ── Resizable panel layout persistence ── */
const LAYOUT_STORAGE_KEY = 'brit_logviewer_layout';

interface PanelLayout {
  /** Sessions panel width as fraction of total width (0.2–0.8) */
  sessionsFraction: number;
  /** Top panels height as fraction of total height (0.2–0.9) */
  topFraction: number;
}

const DEFAULT_LAYOUT: PanelLayout = { sessionsFraction: 0.45, topFraction: 0.75 };

const loadLayout = (): PanelLayout => {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PanelLayout;
      if (parsed.sessionsFraction >= 0.15 && parsed.sessionsFraction <= 0.85
        && parsed.topFraction >= 0.2 && parsed.topFraction <= 0.9) {
        return parsed;
      }
    }
  } catch { /* ignore */ }
  return DEFAULT_LAYOUT;
};

const saveLayout = (layout: PanelLayout) => {
  localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
};

/** Splitter styles */
const splitterBaseSx = {
  backgroundColor: 'transparent',
  transition: 'background-color 0.15s',
  '&:hover': { backgroundColor: 'rgba(91,141,239,0.35)' },
  zIndex: 1,
};

/* ═══════════════════════════════════════════════════════════════════════
 * SessionLogViewer — The structured 3-panel log viewer matching GWT
 * ═══════════════════════════════════════════════════════════════════════ */
const SessionLogViewer = () => {
  const [sessions, setSessions] = useState<SessionLogEntry[]>([]);
  const [records, setRecords] = useState<PacsLogEntry[]>([]);
  const [selectedSession, setSelectedSession] = useState<SessionLogEntry | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<PacsLogEntry | null>(null);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState(false);

  // Sort state for sessions
  const [sessionSort, setSessionSort] = useState<'startTime' | 'message' | 'auth'>('startTime');
  const [sessionSortDir, setSessionSortDir] = useState<'asc' | 'desc'>('desc');

  // Sort state for records
  const [recordSort, setRecordSort] = useState<'time' | 'message'>('time');
  const [recordSortDir, setRecordSortDir] = useState<'asc' | 'desc'>('asc');

  // Resizable panel layout
  const [layout, setLayout] = useState<PanelLayout>(loadLayout);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<'vertical' | 'horizontal' | null>(null);

  /** Persist layout on change */
  useEffect(() => {
    saveLayout(layout);
  }, [layout]);

  /** Mouse-down handlers for drag splitters */
  const onVerticalSplitterDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = 'vertical';
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const onHorizontalSplitterDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = 'horizontal';
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      if (draggingRef.current === 'vertical') {
        const fraction = Math.max(0.15, Math.min(0.85, (e.clientX - rect.left) / rect.width));
        setLayout(prev => ({ ...prev, sessionsFraction: fraction }));
      } else {
        const fraction = Math.max(0.2, Math.min(0.9, (e.clientY - rect.top) / rect.height));
        setLayout(prev => ({ ...prev, topFraction: fraction }));
      }
    };
    const onMouseUp = () => {
      if (draggingRef.current) {
        draggingRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  /** Fetch sessions (optionally filtered by search text) */
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const text = searchText.trim() || undefined;
      const result = await api.getSessionLogs(text);
      setSessions(result ?? []);
      // If searching, also do a record search
      if (text) {
        setSearchMode(true);
        const recs = await api.searchLogRecords(text);
        setRecords(recs ?? []);
        setSelectedSession(null);
        setSelectedRecord(null);
      } else {
        setSearchMode(false);
        // Keep current session selection if still valid
        if (selectedSession) {
          const still = (result ?? []).find(s => s.objKey === selectedSession.objKey);
          if (!still) {
            setSelectedSession(null);
            setRecords([]);
          }
        }
      }
    } catch (e) {
      console.error('Error fetching sessions', e);
    }
    setLoading(false);
  }, [searchText, selectedSession]);

  /** Fetch records for a selected session */
  const fetchRecords = useCallback(async (session: SessionLogEntry) => {
    if (!session.objKey) return;
    try {
      const recs = await api.getLogRecords(session.objKey);
      setRecords(recs ?? []);
      setSelectedRecord(null);
      setSearchMode(false);
    } catch (e) {
      console.error('Error fetching log records', e);
      setRecords([]);
    }
  }, []);

  /** Handle session click */
  const handleSessionClick = useCallback((session: SessionLogEntry) => {
    setSelectedSession(session);
    fetchRecords(session);
  }, [fetchRecords]);

  /** Handle refresh */
  const handleRefresh = useCallback(() => {
    setSelectedSession(null);
    setSelectedRecord(null);
    setRecords([]);
    fetchSessions();
  }, [fetchSessions]);

  /** Handle search on Enter */
  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      fetchSessions();
    }
  }, [fetchSessions]);

  /** Initial load */
  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Sorted sessions */
  const sortedSessions = useMemo(() => {
    const sorted = [...sessions];
    sorted.sort((a, b) => {
      let cmp = 0;
      if (sessionSort === 'startTime') {
        cmp = (a.startTime ?? 0) - (b.startTime ?? 0);
      } else if (sessionSort === 'message') {
        cmp = (a.message ?? '').localeCompare(b.message ?? '');
      } else if (sessionSort === 'auth') {
        cmp = (a.auth ?? '').localeCompare(b.auth ?? '');
      }
      return sessionSortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [sessions, sessionSort, sessionSortDir]);

  /** Sorted records */
  const sortedRecords = useMemo(() => {
    const sorted = [...records];
    sorted.sort((a, b) => {
      let cmp = 0;
      if (recordSort === 'time') {
        cmp = (a.time ?? 0) - (b.time ?? 0);
      } else {
        cmp = (a.message ?? '').localeCompare(b.message ?? '');
      }
      return recordSortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [records, recordSort, recordSortDir]);

  const handleSessionSortClick = (col: typeof sessionSort) => {
    if (sessionSort === col) setSessionSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSessionSort(col); setSessionSortDir(col === 'startTime' ? 'desc' : 'asc'); }
  };

  const handleRecordSortClick = (col: typeof recordSort) => {
    if (recordSort === col) setRecordSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setRecordSort(col); setRecordSortDir('asc'); }
  };

  /** Row style based on session state */
  const getSessionRowStyle = (session: SessionLogEntry) => {
    const isSelected = selectedSession?.objKey === session.objKey;
    if (isSelected) return { backgroundColor: 'rgba(91,141,239,0.25)', cursor: 'pointer' };
    if (session.level === 'SEVERE') return { backgroundColor: 'rgba(255,107,107,0.15)', cursor: 'pointer' };
    if (session.level === 'WARNING') return { backgroundColor: 'rgba(255,169,77,0.12)', cursor: 'pointer' };
    if (!session.active) return { color: '#666', cursor: 'pointer' };
    return { cursor: 'pointer' };
  };

  /** Row style for records */
  const getRecordRowStyle = (rec: PacsLogEntry) => {
    const isSelected = selectedRecord === rec;
    if (isSelected) return { backgroundColor: 'rgba(91,141,239,0.25)', cursor: 'pointer' };
    if (rec.level === 'SEVERE') return { backgroundColor: 'rgba(255,107,107,0.15)', cursor: 'pointer' };
    if (rec.level === 'WARNING') return { backgroundColor: 'rgba(255,169,77,0.12)', cursor: 'pointer' };
    return { cursor: 'pointer' };
  };

  return (
    <Box ref={containerRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search bar */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', px: 1, py: 0.5, gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" sx={{ fontSize: 11 }}>Search String (case sensitive):</Typography>
        <TextField
          size="small"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          sx={{ flex: 1, maxWidth: 400, '& .MuiInputBase-root': { fontSize: 11, height: 26 } }}
        />
        <Button
          size="small" variant="contained"
          onClick={fetchSessions}
          disabled={loading}
          startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none', fontSize: 11 }}
        >
          Search
        </Button>
        <Button
          size="small" variant="outlined"
          onClick={handleRefresh}
          disabled={loading}
          startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none', fontSize: 11 }}
        >
          Refresh
        </Button>
        <Tooltip title="Export selected session">
          <span>
            <IconButton
              size="small"
              disabled={!selectedSession?.objKey}
              onClick={() => {
                if (selectedSession?.objKey) {
                  const sid = localStorage.getItem('brit_sid') || '';
                  window.open(`../PacsObjectService?isLogsExport=true&objKey=${encodeURIComponent(selectedSession.objKey)}&sid=${sid}`, '_blank');
                }
              }}
              sx={{ width: 28, height: 28 }}
            >
              <ExportIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
          {sessions.length} sessions{searchMode ? `, ${records.length} matching records` : ''}
        </Typography>
      </Box>

      {/* Main content: resizable 3-panel layout */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
        {/* Top half — sessions (left) + vertical splitter + records (right) */}
        <Box sx={{ height: `${layout.topFraction * 100}%`, display: 'flex', minHeight: 0 }}>
          {/* Sessions panel */}
          <Box sx={{ width: `${layout.sessionsFraction * 100}%`, display: 'flex', flexDirection: 'column', borderRight: '1px solid', borderColor: 'divider', minHeight: 0 }}>
            <Box sx={{ px: 0.75, py: 0.25, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
              <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>Sessions</Typography>
            </Box>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>
                      Node
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel active={sessionSort === 'startTime'} direction={sessionSortDir} onClick={() => handleSessionSortClick('startTime')}>
                        Start
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel active={sessionSort === 'auth'} direction={sessionSortDir} onClick={() => handleSessionSortClick('auth')}>
                        User
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel active={sessionSort === 'message'} direction={sessionSortDir} onClick={() => handleSessionSortClick('message')}>
                        Message
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSessions.map((sess) => (
                    <TableRow
                      key={sess.objKey}
                      hover
                      onClick={() => handleSessionClick(sess)}
                      sx={getSessionRowStyle(sess)}
                    >
                      <TableCell sx={cellSx}>{sess.nodeName ?? ''}</TableCell>
                      <TableCell sx={cellSx}>{formatDateTime(sess.startTime)}</TableCell>
                      <TableCell sx={cellSx}>{sess.auth ?? ''}</TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: 'normal', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {sess.message ?? ''}
                      </TableCell>
                    </TableRow>
                  ))}
                  {sortedSessions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ ...cellSx, textAlign: 'center', color: 'text.secondary' }}>
                        {loading ? 'Loading...' : 'No sessions'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Vertical splitter (between sessions and records) */}
          <Box
            onMouseDown={onVerticalSplitterDown}
            sx={{
              ...splitterBaseSx,
              width: 5,
              cursor: 'col-resize',
              flexShrink: 0,
            }}
          />

          {/* Records panel */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ px: 0.75, py: 0.25, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
              <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>
                Records{selectedSession ? ` — ${selectedSession.message}` : searchMode ? ' — Search Results' : ''}
              </Typography>
            </Box>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel active={recordSort === 'time'} direction={recordSortDir} onClick={() => handleRecordSortClick('time')}>
                        Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel active={recordSort === 'message'} direction={recordSortDir} onClick={() => handleRecordSortClick('message')}>
                        Message
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRecords.map((rec, idx) => (
                    <TableRow
                      key={`${rec.time}-${idx}`}
                      hover
                      onClick={() => setSelectedRecord(rec)}
                      sx={getRecordRowStyle(rec)}
                    >
                      <TableCell sx={{ ...cellSx, color: getSeverityColor(rec.level), minWidth: 180 }}>
                        {formatTime(rec.time)}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, color: getSeverityColor(rec.level), whiteSpace: 'normal' }}>
                        {rec.message ?? ''}
                      </TableCell>
                    </TableRow>
                  ))}
                  {sortedRecords.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ ...cellSx, textAlign: 'center', color: 'text.secondary' }}>
                        {selectedSession ? 'No records in this session' : searchMode ? 'No matching records' : 'Select a session to view records'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Bottom — Horizontal splitter + Record Detail */}
        <Box
          onMouseDown={onHorizontalSplitterDown}
          sx={{
            ...splitterBaseSx,
            height: 5,
            cursor: 'row-resize',
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minHeight: 60, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 0.75, py: 0.25, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
            <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>Record Detail</Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {selectedRecord ? (
              <Box sx={{ fontFamily: '"Consolas", "Monaco", "Courier New", monospace', fontSize: 11, lineHeight: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 0.5, color: 'text.secondary' }}>
                  <span><b>Level:</b> <span style={{ color: getSeverityColor(selectedRecord.level) }}>{selectedRecord.level}</span></span>
                  <span><b>Time:</b> {formatTime(selectedRecord.time)}</span>
                  {selectedRecord.parentKey && <span><b>Parent:</b> {selectedRecord.parentKey}</span>}
                  {selectedRecord.itemKey && <span><b>Item:</b> {selectedRecord.itemKey}</span>}
                  {selectedRecord.reportKey && <span><b>Report:</b> {selectedRecord.reportKey}</span>}
                </Box>
                <Box sx={{ color: getSeverityColor(selectedRecord.level), mb: 0.5 }}>
                  {selectedRecord.message}
                </Box>
                {selectedRecord.detail && (
                  <Box
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      color: 'text.secondary',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      pt: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {selectedRecord.detail}
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
                Select a record to view details
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};


/* ═══════════════════════════════════════════════════════════════════════
 * RawLogViewer — The tail-f-style raw log viewer (kept as secondary tab)
 * ═══════════════════════════════════════════════════════════════════════ */
const RawLogViewer = () => {
  const [logLines, setLogLines] = useState<string[]>([]);
  const [logLevel, setLogLevel] = useState('INFO');
  const [lines, setLines] = useState('200');
  const [logFile, setLogFile] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const logContainerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const result = await rpcCall<{ lines: string[]; total: number; file: string }>(
        'getLogs',
        { level: logLevel, lines: parseInt(lines, 10) || 200 },
      );
      setLogLines(result.lines ?? []);
      setLogFile(result.file ?? '');
    } catch (e) {
      setLogLines([`[Error fetching logs: ${e instanceof Error ? e.message : 'Unknown error'}]`]);
    }
    setLoading(false);
  }, [logLevel, lines]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchLog, 5000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchLog]);

  useEffect(() => {
    if (autoRefresh && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logLines, autoRefresh]);

  const scrollToBottom = useCallback(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, []);

  const filteredLines = searchTerm
    ? logLines.filter((l) => l.toLowerCase().includes(searchTerm.toLowerCase()))
    : logLines;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Controls */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', px: 1, py: 0.5, gap: 1, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" sx={{ fontSize: 12 }}>Level:</Typography>
        <Select size="small" value={logLevel} onChange={(e) => setLogLevel(e.target.value)} sx={{ fontSize: 12, height: 28 }}>
          <MenuItem value="ALL" sx={{ fontSize: 12 }}>ALL</MenuItem>
          <MenuItem value="FINE" sx={{ fontSize: 12 }}>FINE</MenuItem>
          <MenuItem value="INFO" sx={{ fontSize: 12 }}>INFO</MenuItem>
          <MenuItem value="WARNING" sx={{ fontSize: 12 }}>WARNING</MenuItem>
          <MenuItem value="SEVERE" sx={{ fontSize: 12 }}>SEVERE</MenuItem>
        </Select>
        <Typography variant="body2" sx={{ fontSize: 12 }}>Lines:</Typography>
        <TextField size="small" value={lines} onChange={(e) => setLines(e.target.value)} sx={{ width: 70, '& .MuiInputBase-root': { fontSize: 12, height: 28 } }} />
        <Button size="small" variant="contained" onClick={fetchLog} disabled={loading} startIcon={<RefreshIcon sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', fontSize: 11 }}>
          {loading ? 'Loading...' : 'Fetch'}
        </Button>
        <FormControlLabel
          control={<Checkbox size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} sx={{ p: 0.25 }} />}
          label={<Typography sx={{ fontSize: 11 }}>Auto-refresh (5s)</Typography>}
          sx={{ ml: 0 }}
        />
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <TextField size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Filter logs..." sx={{ width: 180, '& .MuiInputBase-root': { fontSize: 11, height: 26 } }} />
        </Box>
        <Tooltip title="Scroll to bottom">
          <IconButton size="small" onClick={scrollToBottom} sx={{ width: 24, height: 24 }}><ScrollDownIcon sx={{ fontSize: 14 }} /></IconButton>
        </Tooltip>
        {logFile && <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>{logFile}</Typography>}
      </Box>

      {searchTerm && (
        <Box sx={{ px: 1, py: 0.25, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: '#1a1a2e' }}>
          <Typography variant="caption" sx={{ fontSize: 10, color: '#ffa94d' }}>
            Showing {filteredLines.length} of {logLines.length} lines matching &quot;{searchTerm}&quot;
          </Typography>
        </Box>
      )}

      <Box ref={logContainerRef} sx={{ flex: 1, overflow: 'auto', backgroundColor: '#1a1a2e', p: 1 }}>
        {filteredLines.length > 0 ? (
          filteredLines.map((line, idx) => (
            <Typography key={idx} component="div" sx={{
              fontSize: 11, fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
              color: getSeverityColorFromLine(line), whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              lineHeight: 1.4, py: 0.05, '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
            }}>
              {line}
            </Typography>
          ))
        ) : (
          <Typography component="div" sx={{
            fontSize: 11, fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
            color: '#888', textAlign: 'center', py: 4,
          }}>
            {logLines.length === 0 ? 'Click "Fetch" to load server logs.' : 'No lines match the filter.'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};


/* ═══════════════════════════════════════════════════════════════════════
 * LogViewerPanel — Top-level tabbed component
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Log Viewer admin tab — full LogViewPanel equivalent.
 *
 * Two modes:
 *   - **Sessions** (default): Structured 3-panel view with sessions, records, and detail.
 *     Uses getSessionLogs, getLogRecords, and searchLogRecords JSON-RPC endpoints.
 *   - **Raw Log**: Tail-f-style view reading pacs.log directly via getLogs.
 */
export const LogViewerPanel = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: gwtStyles.tabContent }}>
      {/* Title + tab selector */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color: gwtStyles.tabTitle, px: 1.5 }}>
          Log Viewer
        </Typography>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0, fontSize: 11, textTransform: 'none' } }}
        >
          <Tab label="Sessions" />
          <Tab label="Raw Log" />
        </Tabs>
      </Box>

      {/* Tab content */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {activeTab === 0 ? <SessionLogViewer /> : <RawLogViewer />}
      </Box>
    </Box>
  );
};
